import { useTranslation } from 'react-i18next';
import { ExternalLink, FileText, Lock } from 'lucide-react';
import type { CourseDocument } from '@/domain/model/CourseDocument';

const rowClass =
  'flex items-center gap-3 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-4 py-3';

interface DocumentRowProps {
  courseDocument: CourseDocument;
  hasAccess: boolean;
}

const DocumentRow = ({ courseDocument, hasAccess }: DocumentRowProps) => {
  const { t } = useTranslation();

  if (!hasAccess) {
    return (
      <li className={rowClass}>
        <Lock className="h-4 w-4 shrink-0 text-[var(--color-ink-faint)]" />
        <span className="min-w-0 flex-1 truncate text-[var(--color-ink-faint)]">
          {courseDocument.fileName}
        </span>
        <span className="shrink-0 text-sm text-[var(--color-ink-faint)]">
          {t('coursePlayer.documentLocked')}
        </span>
      </li>
    );
  }

  if (!courseDocument.url) {
    return (
      <li className={rowClass}>
        <FileText className="h-4 w-4 shrink-0 text-[var(--color-ink-faint)]" />
        <span className="min-w-0 flex-1 truncate text-[var(--color-ink-faint)]">
          {courseDocument.fileName}
        </span>
        <span role="alert" className="shrink-0 text-sm text-[var(--color-bad)]">
          {t('coursePlayer.documentUnavailable')}
        </span>
      </li>
    );
  }

  return (
    <li>
      <a
        href={courseDocument.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${rowClass} transition-colors hover:bg-[var(--color-surface-2)]`}
      >
        <FileText className="h-4 w-4 shrink-0 text-[var(--color-accent)]" />
        <span className="min-w-0 flex-1 truncate text-[var(--color-ink)]">
          {courseDocument.fileName}
        </span>
        <ExternalLink className="h-4 w-4 shrink-0 text-[var(--color-ink-faint)]" />
        <span className="sr-only">{t('coursePlayer.openDocument')}</span>
      </a>
    </li>
  );
};

interface CourseDocumentListProps {
  documents: CourseDocument[];
  hasAccess: boolean;
}

export const CourseDocumentList = ({ documents, hasAccess }: CourseDocumentListProps) => {
  const { t } = useTranslation();

  if (documents.length === 0) {
    return null;
  }

  return (
    <section aria-labelledby="course-documents-title" className="mt-8">
      <h3
        id="course-documents-title"
        className="mb-3 text-lg font-semibold text-[var(--color-ink)]"
      >
        {t('coursePlayer.documents')}
      </h3>
      <ul className="flex flex-col gap-2">
        {documents.map((courseDocument) => (
          <DocumentRow
            key={courseDocument.id}
            courseDocument={courseDocument}
            hasAccess={hasAccess}
          />
        ))}
      </ul>
    </section>
  );
};
